import { useQueryClient } from "@tanstack/react-query";
import useAPI, { useAPIMutation } from "@/services/api/hook";
import { ExtendedUseQueryResult } from "@/services/api/types";
import {
  ApolloCallOutcome,
  ApolloContactImportSettings,
  ApolloDialerSettings,
  ApolloList,
  ApolloListModality,
  ApolloListSyncSettings,
  ApolloSettings
} from "@/entities/apollo/types";

const SETTINGS_KEY = "apollo/settings";

// The lists and outcomes endpoints answer 409 when Apollo isn't connected.
export const isApolloNotConnected = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status ===
  409;

export const useApolloSettings = (): ExtendedUseQueryResult<ApolloSettings> =>
  useAPI(SETTINGS_KEY);

export const useApolloLists = (
  modality: ApolloListModality
): ExtendedUseQueryResult<ApolloList[]> =>
  useAPI(
    "apollo/lists",
    { queryParams: { modality } },
    { select: (response: { lists: ApolloList[] }) => response.lists }
  );

export const useApolloCallOutcomes =
  (): ExtendedUseQueryResult<ApolloCallOutcome[]> =>
    useAPI("apollo/callOutcomes", undefined, {
      select: (response: { callOutcomes: ApolloCallOutcome[] }) =>
        response.callOutcomes
    });

const useSaveApolloSection = <T>(endpoint: string) => {
  const queryClient = useQueryClient();
  // Returning the refetch keeps the save pending until the saved state shows.
  return useAPIMutation<{ data: T }, T>(endpoint, { method: "PUT" }, () =>
    queryClient.invalidateQueries({ queryKey: [SETTINGS_KEY] })
  );
};

export const useSaveApolloListSync = () =>
  useSaveApolloSection<ApolloListSyncSettings>("apollo/settings/listSync");

export const useSaveApolloDialer = () =>
  useSaveApolloSection<ApolloDialerSettings>("apollo/settings/dialer");

export const useSaveApolloContactImport = () =>
  useSaveApolloSection<ApolloContactImportSettings>(
    "apollo/settings/contactImport"
  );
